import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from './apiBaseUrl';
import './AdminDashboard.css';

const ADMIN_SESSION_KEY = 'admin_console_session';

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function StatusTag({ tone = 'pending', children }) {
  return <span className={`status-tag status-${tone}`}>{children}</span>;
}

function JsonBlock({ data }) {
  return (
    <pre className="crif-json-block">
      {JSON.stringify(data || {}, null, 2)}
    </pre>
  );
}

function CopyButton({ data }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data || {}, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button className="crif-copy-btn" onClick={handleCopy} title="Copy JSON">
      {copied ? '✓ Copied' : '⎘ Copy'}
    </button>
  );
}

function CollapsibleJsonCard({ title, icon, data }) {
  const [open, setOpen] = React.useState(false);
  const isEmpty = !data || Object.keys(data).length === 0;
  return (
    <div className={`crif-json-card ${open ? 'is-open' : ''}`}>
      <button className="crif-json-toggle" onClick={() => setOpen((p) => !p)}>
        <span className="crif-json-toggle-left">
          <span className="crif-section-icon">{icon}</span>
          <span className="crif-json-title-text">{title}</span>
          {isEmpty && <span className="crif-json-empty-badge">empty</span>}
        </span>
        <span className="crif-json-toggle-right">
          {!isEmpty && <CopyButton data={data} />}
          <span className={`crif-chevron ${open ? 'up' : ''}`}>▾</span>
        </span>
      </button>
      {open && !isEmpty && (
        <div className="crif-json-body">
          <JsonBlock data={data} />
        </div>
      )}
      {open && isEmpty && (
        <div className="crif-json-body crif-json-no-data">No data available</div>
      )}
    </div>
  );
}

function InfoRow({ icon, label, value, mono }) {
  return (
    <div className="crif-info-row">
      <span className="crif-info-icon">{icon}</span>
      <span className="crif-info-label">{label}</span>
      <span className={`crif-info-value ${mono ? 'mono' : ''}`}>{value || '—'}</span>
    </div>
  );
}

function ReportPreview({ baseUrl, report }) {
  const [loading, setLoading] = React.useState(true);
  const isPdf = report.has_pdf_report && !report.has_html_report;
  const src = `${baseUrl}${report.view_url}`;
  return (
    <div className="crif-preview-wrapper">
      <div className="crif-preview-bar">
        <span className="crif-preview-label">
          {isPdf ? '📄 PDF Report' : '🌐 HTML Report'} — {report.inquiry_ref_no || `Report #${report.id}`}
        </span>
        <span className="crif-preview-meta">{loading ? 'Loading…' : 'Loaded'}</span>
      </div>
      {isPdf ? (
        <object
          data={src}
          type="application/pdf"
          className="crif-preview-frame"
          title="CRIF PDF Report"
          onLoad={() => setLoading(false)}
        >
          <div className="crif-preview-fallback">
            <p>Your browser cannot display this PDF inline.</p>
            <a href={src} target="_blank" rel="noreferrer" className="setup-btn crif-action-link">
              Open PDF
            </a>
          </div>
        </object>
      ) : (
        <iframe
          src={src}
          className="crif-preview-frame"
          title="CRIF HTML Report"
          sandbox="allow-same-origin allow-scripts"
          onLoad={() => setLoading(false)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   Offer Decision Audit Logs Tab
═══════════════════════════════════════════════════════ */
function getDecisionDisplayState(row = {}) {
  const status = String(row.decision_status || row.status || '').trim().toUpperCase();
  if (Boolean(row.approved)) return 'approved';
  if (status === 'PENDING' || status === 'AA_PENDING') return 'pending';
  return 'declined';
}

function DecisionBadge({ row }) {
  const displayState = getDecisionDisplayState(row);
  const label = displayState === 'approved'
    ? 'Approved'
    : displayState === 'pending'
      ? 'Pending'
      : 'Declined';
  return (
    <span className={`oda-badge oda-badge--${displayState}`}>
      {label}
    </span>
  );
}

function RuleChip({ value, color }) {
  if (!value) return <span className="oda-null">—</span>;
  return <code className={`oda-chip oda-chip--${color || 'gray'}`}>{value}</code>;
}

function AuditRow({ row, index }) {
  const [expanded, setExpanded] = React.useState(false);
  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '₹0';
  const fmtScore = (s) => s != null ? String(s) : '—';
  const metadata = row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
  const clientFacingMessage = metadata.clientFacingMessage || '';
  const clientFacingSalaryAmount = metadata.clientFacingSalaryAmount ?? metadata.salaryAmount ?? null;
  const displaySalaryAmount = row.salary_amount != null ? row.salary_amount : clientFacingSalaryAmount;
  const decisionDisplayState = getDecisionDisplayState(row);
  const dt = (v) => {
    if (!v) return '—';
    return new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const hasRuleDetail = row.decision_basis_summary || row.decision_explanation_text || row.reason || row.decision_signal_summary || clientFacingMessage;
  return (
    <>
      <tr className={`oda-row oda-row--${decisionDisplayState}`}>
        <td className="oda-cell oda-cell--num">{index}</td>
        <td className="oda-cell oda-cell--time">
          <div className="oda-time">{dt(row.created_at)}</div>
          <div className="oda-sub">#{row.id}</div>
        </td>
        <td className="oda-cell">
          <div className="oda-identity-name">{row.segment_code ? <RuleChip value={row.segment_code} color="blue" /> : null}</div>
          <div className="oda-identity-name" style={{ marginTop: row.segment_code ? 4 : 0 }}>
            {row.product_code ? <RuleChip value={row.product_code} color="purple" /> : null}
          </div>
          <div className="oda-mono">{row.mobile_number || '—'}</div>
          <div className="oda-mono oda-pan">{row.pan_number || '—'}</div>
        </td>
        <td className="oda-cell">
          <DecisionBadge row={row} />
          <div className="oda-sub" style={{ marginTop: 4 }}>{row.decision_status || ''}</div>
        </td>
        <td className="oda-cell oda-cell--amount">
          <div className={`oda-amount ${row.approved ? 'oda-amount--green' : 'oda-amount--gray'}`}>{fmt(row.offered_amount)}</div>
          <div className="oda-sub">{row.source_context || '—'}</div>
        </td>
        <td className="oda-cell">
          <div className="oda-score-row">
            <span className="oda-score-label">Score</span>
            <span className="oda-score-val">{fmtScore(row.credit_score)}</span>
          </div>
          <div className="oda-score-row">
            <span className="oda-score-label">Salary</span>
            <span className="oda-score-val">{fmt(displaySalaryAmount)}</span>
          </div>
          {row.salary_source && <div className="oda-sub">{row.salary_source}</div>}
        </td>
        <td className="oda-cell">
          <RuleChip value={row.rule_code} color="orange" />
          <div className="oda-sub" style={{ marginTop: 4 }}>Engine: {row.engine_version || '—'}</div>
        </td>
        <td className="oda-cell oda-cell--reason">
          {[row.reason, row.decision_explanation_text, row.decision_basis_summary]
            .concat(clientFacingMessage ? [clientFacingMessage] : [])
            .filter(Boolean)
            .length > 0
            ? [row.reason, row.decision_explanation_text, row.decision_basis_summary, clientFacingMessage]
                .filter(Boolean)
                .map((txt, i, arr) => (
                  <div
                    key={i}
                    className="oda-reason-text"
                    style={i < arr.length - 1 ? { marginBottom: 6, paddingBottom: 6, borderBottom: '1px dashed #e2e8f0' } : {}}
                  >{txt}</div>
                ))
            : <span className="oda-null">—</span>}
        </td>
        <td className="oda-cell oda-cell--actions">
          {hasRuleDetail && (
            <button
              className={`oda-expand-btn ${expanded ? 'open' : ''}`}
              onClick={() => setExpanded(p => !p)}
              title={expanded ? 'Collapse rule trace' : 'Expand rule trace'}
            >
              {expanded ? '▴ Collapse' : '▾ Trace'}
            </button>
          )}
        </td>
      </tr>
      {expanded && hasRuleDetail && (
        <tr className="oda-detail-row">
          <td colSpan={9} className="oda-detail-cell">
            <div className="oda-detail-grid">
              {row.decision_explanation_title && (
                <div className="oda-detail-block oda-detail-block--full">
                  <div className="oda-detail-label">📋 Decision Title</div>
                  <div className="oda-detail-value oda-detail-value--bold">{row.decision_explanation_title}</div>
                </div>
              )}
              {row.decision_explanation_text && (
                <div className="oda-detail-block oda-detail-block--full">
                  <div className="oda-detail-label">🔍 Explanation</div>
                  <div className="oda-detail-value">{row.decision_explanation_text}</div>
                </div>
              )}
              {row.decision_basis_summary && (
                <div className="oda-detail-block oda-detail-block--full">
                  <div className="oda-detail-label">📊 Basis Summary</div>
                  <div className="oda-detail-value">{row.decision_basis_summary}</div>
                </div>
              )}
              {row.reason && (
                <div className="oda-detail-block">
                  <div className="oda-detail-label">⚠️ Reason</div>
                  <div className="oda-detail-value">{row.reason}</div>
                </div>
              )}
              {row.decision_signal_summary && (
                <div className="oda-detail-block">
                  <div className="oda-detail-label">📡 Signal Summary</div>
                  <div className="oda-detail-value">{row.decision_signal_summary}</div>
                </div>
              )}
              {clientFacingMessage && (
                <div className="oda-detail-block oda-detail-block--full">
                  <div className="oda-detail-label">🧑 Client View</div>
                  <div className="oda-detail-value">{clientFacingMessage}</div>
                </div>
              )}
              {row.decision_method && (
                <div className="oda-detail-block">
                  <div className="oda-detail-label">⚙️ Method</div>
                  <code className="oda-detail-code">{row.decision_method}</code>
                </div>
              )}
              {row.salary_detection_mode && (
                <div className="oda-detail-block">
                  <div className="oda-detail-label">💼 Salary Detection</div>
                  <code className="oda-detail-code">{row.salary_detection_mode}</code>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function OfferAuditTab({ baseUrl, token }) {
  const getHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};
  const [rows, setRows]         = React.useState([]);
  const [total, setTotal]       = React.useState(0);
  const [loading, setLoading]   = React.useState(false);
  const [error, setError]       = React.useState('');
  const [search, setSearch]     = React.useState('');
  const [decision, setDecision] = React.useState('ALL');
  const [page, setPage]         = React.useState(1);
  const PAGE_SIZE = 25;

  const load = React.useCallback(async (pg = 1, q = search, dec = decision) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(pg), pageSize: String(PAGE_SIZE), sortBy: 'created_at', sortDir: 'DESC' });
      if (q) params.set('search', q);
      if (dec !== 'ALL') params.set('decision', dec);
      const res = await fetch(`${baseUrl}/api/admin/offer-decision-audit-logs?${params}`, { headers: getHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load audit logs');
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total || 0));
      setPage(pg);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [baseUrl, search, decision]); // eslint-disable-line

  React.useEffect(() => { load(1, search, decision); }, []); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    load(1, search, decision);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const approvedCount = rows.filter(r => getDecisionDisplayState(r) === 'approved').length;
  const pendingCount = rows.filter(r => getDecisionDisplayState(r) === 'pending').length;
  const declinedCount = rows.filter(r => getDecisionDisplayState(r) === 'declined').length;

  return (
    <div className="oda-tab">
      {/* Header */}
      <div className="oda-header">
        <div>
          <h2 className="oda-title">Offer Decision Audit Logs</h2>
          <p className="oda-desc">Clean review table for decision outcome, applicant identity, offer amount, score inputs, and rule trace.</p>
        </div>
        <div className="oda-header-stats">
          <div className="oda-stat oda-stat--total">
            <span className="oda-stat-val">{total.toLocaleString('en-IN')}</span>
            <span className="oda-stat-lbl">Total</span>
          </div>
          <div className="oda-stat oda-stat--approved">
            <span className="oda-stat-val">{approvedCount}</span>
            <span className="oda-stat-lbl">Approved</span>
          </div>
          <div className="oda-stat oda-stat--pending">
            <span className="oda-stat-val">{pendingCount}</span>
            <span className="oda-stat-lbl">Pending</span>
          </div>
          <div className="oda-stat oda-stat--declined">
            <span className="oda-stat-val">{declinedCount}</span>
            <span className="oda-stat-lbl">Declined</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <form className="oda-toolbar" onSubmit={handleSearch}>
        <div className="oda-search-wrap">
          <span className="oda-search-icon">🔍</span>
          <input
            className="oda-search"
            placeholder="Search mobile, PAN, rule code, segment…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="oda-filter-group">
          {['ALL', 'APPROVED', 'DECLINED'].map(d => (
            <button
              key={d}
              type="button"
              className={`oda-filter-btn ${decision === d ? 'active' : ''} oda-filter-btn--${d.toLowerCase()}`}
              onClick={() => { setDecision(d); load(1, search, d); }}
            >{d}</button>
          ))}
        </div>
        <button className="oda-refresh-btn" type="submit" disabled={loading}>↻ Refresh</button>
      </form>

      {/* States */}
      {error && <div className="oda-error">⚠ {error}</div>}
      {loading && <div className="oda-loading"><span className="oda-spinner" />Loading audit logs…</div>}

      {/* Table */}
      {!loading && !error && (
        <div className="oda-table-wrap">
          <table className="oda-table">
            <thead>
              <tr>
                <th className="oda-th">S.No</th>
                <th className="oda-th">Decision Time</th>
                <th className="oda-th">Identity</th>
                <th className="oda-th">Decision</th>
                <th className="oda-th">Offer</th>
                <th className="oda-th">Score &amp; Salary</th>
                <th className="oda-th">Rule Trace</th>
                <th className="oda-th">Reason</th>
                <th className="oda-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={9} className="oda-empty">No audit records found.</td></tr>
              ) : rows.map((row, i) => (
                <AuditRow key={row.id} row={row} index={(page - 1) * PAGE_SIZE + i + 1} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="oda-pagination">
          <button className="oda-page-btn" disabled={page <= 1} onClick={() => load(page - 1)}>‹ Prev</button>
          <span className="oda-page-info">Page {page} of {totalPages} · {total.toLocaleString('en-IN')} records</span>
          <button className="oda-page-btn" disabled={page >= totalPages} onClick={() => load(page + 1)}>Next ›</button>
        </div>
      )}
    </div>
  );
}

function AdminDashboard() {
  const baseUrl = useMemo(() => getApiBaseUrl(), []);
  const [activeTab, setActiveTab] = useState('crif');
  const [adminSession, setAdminSession] = useState(() => {
    try {
      const raw = localStorage.getItem(ADMIN_SESSION_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || !String(parsed.token || '').trim()) return null;
      return parsed;
    } catch (_error) {
      return null;
    }
  });
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [search, setSearch] = useState('');
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState('');
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const getAdminAuthHeaders = useCallback((extraHeaders = {}) => {
    const token = String(adminSession?.token || '').trim();
    return token
      ? { ...extraHeaders, Authorization: `Bearer ${token}` }
      : { ...extraHeaders };
  }, [adminSession]);

  const clearAdminSession = useCallback(() => {
    localStorage.removeItem(ADMIN_SESSION_KEY);
    setAdminSession(null);
    setSelectedReportId(null);
    setSelectedReport(null);
  }, []);

  const handleAdminAuthFailure = useCallback(() => {
    clearAdminSession();
    setLoginError('Your admin session expired. Please sign in again.');
  }, [clearAdminSession]);

  useEffect(() => {
    if (!adminSession) return;
    let cancelled = false;

    const loadReports = async () => {
      setReportsLoading(true);
      setReportsError('');
      try {
        const query = new URLSearchParams({
          page: '1',
          pageSize: '50',
          sortBy: 'created_at',
          sortDir: 'DESC'
        });
        if (search.trim()) query.set('search', search.trim());
        const response = await fetch(`${baseUrl}/api/admin/crif-reports?${query.toString()}`, {
          headers: getAdminAuthHeaders()
        });
        const data = await response.json();
        if (response.status === 401) {
          handleAdminAuthFailure();
          return;
        }
        if (!response.ok || !data.success) {
          throw new Error(data.message || data.error || 'Failed to load CRIF reports');
        }
        if (cancelled) return;
        const nextRows = Array.isArray(data.rows) ? data.rows : [];
        setReports(nextRows);
        if (!selectedReportId && nextRows[0]?.id) {
          setSelectedReportId(nextRows[0].id);
        } else if (selectedReportId && !nextRows.some((item) => item.id === selectedReportId)) {
          setSelectedReportId(nextRows[0]?.id || null);
        }
      } catch (error) {
        if (!cancelled) {
          setReportsError(error.message);
          setReports([]);
        }
      } finally {
        if (!cancelled) {
          setReportsLoading(false);
        }
      }
    };

    loadReports();
    return () => {
      cancelled = true;
    };
  }, [adminSession, baseUrl, search, selectedReportId, getAdminAuthHeaders, handleAdminAuthFailure]);

  useEffect(() => {
    if (!adminSession || !selectedReportId) {
      setSelectedReport(null);
      setPreviewOpen(false);
      return;
    }
    let cancelled = false;

    const loadDetail = async () => {
      setDetailLoading(true);
      setDetailError('');
      try {
        const response = await fetch(`${baseUrl}/api/admin/crif-reports/${selectedReportId}`, {
          headers: getAdminAuthHeaders()
        });
        const data = await response.json();
        if (response.status === 401) {
          handleAdminAuthFailure();
          return;
        }
        if (!response.ok || !data.success) {
          throw new Error(data.message || data.error || 'Failed to load CRIF report details');
        }
        if (!cancelled) {
          setSelectedReport(data.report || null);
        }
      } catch (error) {
        if (!cancelled) {
          setDetailError(error.message);
          setSelectedReport(null);
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [adminSession, baseUrl, selectedReportId, getAdminAuthHeaders, handleAdminAuthFailure]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const response = await fetch(`${baseUrl}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Admin login failed');
      }
      if (!String(data.token || '').trim()) {
        throw new Error('Admin session token was not issued');
      }
      const session = {
        username: data.username || loginForm.username,
        role: data.role || 'VIEW_ONLY',
        permissions: data.permissions || null,
        token: data.token || '',
        expiresAt: data.expiresAt || null,
        sessionVersion: data.sessionVersion || 1
      };
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(session));
      setAdminSession(session);
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    clearAdminSession();
  };

  if (!adminSession) {
    return (
      <div className="admin-login-screen">
        {loginLoading ? (
          <div className="app-page-loader-overlay" aria-live="polite" aria-busy="true">
            <div className="app-page-loader-card">
              <span className="app-page-loader-spinner" aria-hidden="true"></span>
              <p>Signing you in...</p>
            </div>
          </div>
        ) : null}
        <form className="admin-login-card" onSubmit={handleLogin}>
          <div className="admin-login-badge">Admin Console</div>
          <h1>Sign In</h1>
          <p>Use your admin credentials to access CRIF data and operations tools.</p>
          <label className="admin-login-field">
            <span>Username</span>
            <input
              type="text"
              value={loginForm.username}
              onChange={(event) => setLoginForm((prev) => ({ ...prev, username: event.target.value }))}
              required
            />
          </label>
          <label className="admin-login-field">
            <span>Password</span>
            <input
              type="password"
              value={loginForm.password}
              onChange={(event) => setLoginForm((prev) => ({ ...prev, password: event.target.value }))}
              required
            />
          </label>
          {loginError ? <div className="admin-login-error">{loginError}</div> : null}
          <button className="setup-btn" type="submit" disabled={loginLoading}>
            {loginLoading ? 'Signing In...' : 'Login'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-logo">
          <div className="logo-icon">A</div>
          <div>
            <div className="logo-text">Admin</div>
            <div className="admin-subtitle">{adminSession.username}</div>
          </div>
        </div>

        <div
          className={`nav-item ${activeTab === 'crif' ? 'active' : ''}`}
          onClick={() => setActiveTab('crif')}
          role="button"
          tabIndex={0}
        >
          <span>📊 CRIF Data</span>
        </div>

        <div
          className={`nav-item ${activeTab === 'offer-audit' ? 'active' : ''}`}
          onClick={() => setActiveTab('offer-audit')}
          role="button"
          tabIndex={0}
        >
          <span>🧮 Decision Audit</span>
        </div>

        <button className="admin-logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </aside>

      <main className="admin-content">
        {activeTab === 'offer-audit' && (
          <OfferAuditTab baseUrl={baseUrl} token={adminSession?.token} />
        )}

        {activeTab === 'crif' && (<>
        <div className="admin-header">
          <div className="header-text">
            <h1>CRIF Data</h1>
            <p>Browse stored CRIF bureau checks, inspect parsed JSON, and open the stored report file.</p>
          </div>
        </div>

        <div className="crif-toolbar">
          <input
            className="crif-search-input"
            type="text"
            placeholder="Search by mobile, PAN, inquiry ref, applicant"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="crif-layout">
          <section className="table-container crif-list-panel">
            <div className="crif-panel-header">
              <h2>Reports</h2>
              <span>{reports.length} items</span>
            </div>
            {reportsLoading ? <div className="crif-empty">Loading reports...</div> : null}
            {reportsError ? <div className="crif-empty crif-error">{reportsError}</div> : null}
            {!reportsLoading && !reportsError && !reports.length ? <div className="crif-empty">No CRIF reports found.</div> : null}
            {!reportsLoading && reports.length ? (
              <div className="crif-list">
                {reports.map((report) => (
                  <button
                    key={report.id}
                    className={`crif-list-item ${selectedReportId === report.id ? 'active' : ''}`}
                    onClick={() => setSelectedReportId(report.id)}
                  >
                    <div className="crif-list-top">
                      <strong>{report.applicant_name || report.user_full_name || report.pan_number || `Report ${report.id}`}</strong>
                      <StatusTag tone={report.bureau_status === 'SUCCESS' ? 'active' : 'pending'}>
                        {report.bureau_status || 'UNKNOWN'}
                      </StatusTag>
                    </div>
                    <div className="crif-list-meta">
                      <span>{report.mobile_number || '-'}</span>
                      <span>{report.credit_score ?? '-'}</span>
                    </div>
                    <div className="crif-list-submeta">
                      <span>{report.inquiry_ref_no || '-'}</span>
                      <span>{formatDateTime(report.created_at)}</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section className="table-container crif-detail-panel">
            {detailLoading ? <div className="crif-empty">Loading CRIF detail...</div> : null}
            {detailError ? <div className="crif-empty crif-error">{detailError}</div> : null}
            {!detailLoading && !detailError && !selectedReport ? <div className="crif-empty">Select a report to view details.</div> : null}
            {!detailLoading && selectedReport ? (
              <div className="crif-detail-body">
                <div className="crif-panel-header">
                  <div>
                    <h2>{selectedReport.applicant_name || selectedReport.user_full_name || 'CRIF Detail'}</h2>
                    <p className="crif-detail-caption">Inquiry Ref: {selectedReport.inquiry_ref_no || '-'}</p>
                  </div>
                  <div className="crif-action-row">
                    <button
                      className={`setup-btn crif-preview-toggle-btn ${previewOpen ? 'active' : ''}`}
                      onClick={() => setPreviewOpen((p) => !p)}
                    >
                      {previewOpen ? '✕ Close Preview' : '👁 Preview Report'}
                    </button>
                    <a
                      className="setup-btn crif-action-link crif-btn-outline"
                      href={`${baseUrl}${selectedReport.download_url}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      ⬇ Download
                    </a>
                  </div>
                </div>

                {/* ── Inline Report Preview ── */}
                {previewOpen && (
                  <ReportPreview baseUrl={baseUrl} report={selectedReport} />
                )}

                {/* ── Customer Information ── */}
                <div className="crif-section-heading">
                  <span>👤 Customer Information</span>
                </div>
                <div className="crif-info-card">
                  <InfoRow icon="📱" label="Mobile" value={selectedReport.mobile_number} mono />
                  <InfoRow icon="🪪" label="PAN" value={selectedReport.pan_number} mono />
                  <InfoRow icon="📊" label="Credit Score" value={selectedReport.credit_score != null ? String(selectedReport.credit_score) : null} />
                  <InfoRow icon="📄" label="Report Type" value={selectedReport.decoded_report_type || (selectedReport.has_html_report ? 'HTML' : null)} />
                  <InfoRow icon="📅" label="Bureau Status" value={selectedReport.bureau_status} />
                  <InfoRow icon="🔖" label="Inquiry Ref" value={selectedReport.inquiry_ref_no} mono />
                </div>

                {/* ── Documents & Payloads ── */}
                <div className="crif-section-heading">
                  <span>📂 Documents &amp; Payloads</span>
                </div>
                <div className="crif-json-sections">
                  <CollapsibleJsonCard
                    title="Parsed Summary"
                    icon="📋"
                    data={selectedReport.parsed_summary}
                  />
                  <CollapsibleJsonCard
                    title="Request Payload"
                    icon="📤"
                    data={selectedReport.request_payload}
                  />
                  <CollapsibleJsonCard
                    title="Response Payload"
                    icon="📥"
                    data={selectedReport.response_payload}
                  />
                </div>
              </div>
            ) : null}
          </section>
        </div>
        </>)}
      </main>
    </div>
  );
}

export default AdminDashboard;
